import { ChainType, StatusResponse } from "@0xsquid/squid-types";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { transactionEndStatuses } from "../../core/constants";
import { keys } from "../../core/queries/queries-keys";
import {
  SwapTransactionForStatus,
  TransactionStatus,
} from "../../core/types/transaction";
import {
  fetchSwapTransactionStatus,
  getSwapTxStatusRefetchInterval,
  getTransactionEndStatus,
  getTransactionStatus,
  is404Error,
} from "../../services";
import { WidgetEvents } from "../../services/internal/eventService";
import { useSquidChains } from "../chains/useSquidChains";
import { useConfigStore } from "../store/useSquidStore";

interface UseSwapStatusQueryParams {
  transaction?: SwapTransactionForStatus;
  retry?: number;
  refetchOnWindowFocus?: "always" | boolean;
  enabled?: boolean;
  /**
   * Fires on every successful status response, with the raw status string.
   * Use for non-terminal transitions (e.g. a deposit being received).
   */
  onStatus?: (params: {
    status: string;
    statusResponse: StatusResponse;
  }) => void;
  /**
   * Fires on terminal status transitions
   */
  onEndStatus?: (params: {
    status: TransactionStatus;
    statusResponse: StatusResponse;
  }) => void;
  onNotFound?: () => void;
  onError?: () => void;
}

export const useSwapStatusQuery = ({
  transaction,
  retry = 25,
  refetchOnWindowFocus = "always",
  enabled = true,
  onStatus,
  onEndStatus,
  onNotFound,
  onError,
}: UseSwapStatusQueryParams) => {
  const config = useConfigStore((state) => state.config);
  const isInitialized = useConfigStore((state) => state.isInitialized);
  const [isTransactionComplete, setIsTransactionComplete] = useState(false);
  const [refetchInterval, setRefetchInterval] = useState(
    getSwapTxStatusRefetchInterval(transaction)
  );
  const { getChainType } = useSquidChains();

  const fetchTransactionStatusWithLatestConfig = useCallback(async () => {
    const latestConfig = useConfigStore.getState().config;
    return fetchSwapTransactionStatus({
      transaction,
      integratorId: latestConfig.integratorId,
      apiUrl: latestConfig.apiUrl!,
    });
  }, [transaction]);

  const transactionStatusQuery = useQuery(
    keys().swapTransactionStatus(transaction?.transactionId),
    fetchTransactionStatusWithLatestConfig,
    {
      enabled:
        enabled &&
        transaction?.transactionId !== "0" &&
        !!transaction?.transactionId &&
        !!transaction.fromAddress &&
        !!config.apiUrl &&
        transaction !== undefined &&
        !isTransactionComplete &&
        isInitialized,
      refetchInterval(statusResponse) {
        if (
          statusResponse &&
          transactionEndStatuses.includes(
            getTransactionStatus(statusResponse) ?? ""
          )
        ) {
          return false;
        }

        return refetchInterval;
      },
      retryDelay:
        getChainType(transaction?.fromChain) === ChainType.COSMOS ? 5000 : 3000,
      retry:
        getChainType(transaction?.fromChain) === ChainType.COSMOS ? 6 : retry,
      refetchOnWindowFocus,
      onSuccess: (statusResponse) => {
        WidgetEvents.getInstance().dispatchSwapStatus(
          statusResponse.squidTransactionStatus ?? ""
        );

        onStatus?.({
          status: getTransactionStatus(statusResponse) ?? "",
          statusResponse,
        });

        const endStatus = getTransactionEndStatus({ statusResponse });

        if (endStatus) {
          setIsTransactionComplete(true);
          onEndStatus?.({ status: endStatus, statusResponse });
        }
      },
      onError: (error: Error) => {
        if (is404Error(error.cause)) {
          onNotFound?.();
          return;
        }

        setRefetchInterval(-1);
        setIsTransactionComplete(true);
        onError?.();
      },
    }
  );

  return {
    transactionStatusQuery,
  };
};
